import React, { useCallback, useEffect, useState } from 'react';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabsContext,
  Heading,
  Text,
  Loader,
} from '@vibe/core';
import {
  getUsers,
  getAppSettings,
  updateAppSettings,
  type UserAccess,
} from '../../api/settings.api';
import { fetchMondayUsers } from '../../api/monday.api';
import ManageAccessTab from './ManageAccessTab';
import RestrictUsersTab from './RestrictUsersTab';
import AppAdminsTab from './AppAdminsTab';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Permission management panel with 3 sub-tabs:
 * - Manage Access: control who can access the app, toggle "All users", add/remove users
 * - Restrict Users: manage restricted users
 * - App Admins: manage app administrators
 *
 * The backend only stores mondayUserId + role flags.
 * User names/emails are resolved here via the monday.com SDK.
 */
const PermissionManagementPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [allUsersEnabled, setAllUsersEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── Fetch users + app settings + monday user profiles ────────────────

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersData, appSettings, mondayUsers] = await Promise.all([
        getUsers(),
        getAppSettings(),
        fetchMondayUsers(),
      ]);

      // Build a lookup map: monday user ID → { name, email }
      const mondayUserMap = new Map(
        mondayUsers.map((u) => [
          String(u.id),
          { name: u.name, email: u.email, title: u.title },
        ]),
      );

      // Enrich backend records with monday.com user names/emails
      const enriched: UserAccess[] = usersData.map((u) => {
        const mu = mondayUserMap.get(u.mondayUserId);
        return {
          ...u,
          userName: mu?.name || u.userName || '',
          email: mu?.email || u.email || '',
        };
      });

      setUsers(enriched);
      setAllUsersEnabled(appSettings.allUsersAccessEnabled);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Toggle "All users" and persist to backend ──────────────────────────

  const handleToggleAllUsers = useCallback(async (enabled: boolean) => {
    setAllUsersEnabled(enabled);
    try {
      await updateAppSettings({ allUsersAccessEnabled: enabled });
    } catch (err) {
      // Revert on failure
      setAllUsersEnabled(!enabled);
      const message =
        err instanceof Error ? err.message : 'Failed to update access setting';
      setError(message);
    }
  }, []);

  // ─── User list helpers ──────────────────────────────────────────────────

  const updateUser = useCallback((updated: UserAccess) => {
    setUsers((prev) => {
      const exists = prev.some((u) => u.mondayUserId === updated.mondayUserId);
      if (exists) {
        // Preserve existing name/email when updating role flags
        return prev.map((u) =>
          u.mondayUserId === updated.mondayUserId
            ? { ...updated, userName: updated.userName || u.userName, email: updated.email || u.email }
            : u,
        );
      }
      // User not in list yet — add it (e.g. restrict/admin a new user)
      return [...prev, updated];
    });
  }, []);

  const addUser = useCallback((user: UserAccess) => {
    setUsers((prev) => {
      const exists = prev.some((u) => u.mondayUserId === user.mondayUserId);
      if (exists) {
        return prev.map((u) =>
          u.mondayUserId === user.mondayUserId ? user : u,
        );
      }
      return [...prev, user];
    });
  }, []);

  const removeUser = useCallback((mondayUserId: string) => {
    setUsers((prev) => prev.filter((u) => u.mondayUserId !== mondayUserId));
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="rp-permission-mgmt">
        <Heading type={Heading.types.H3}>Permission Management</Heading>
        <div className="rp-permission-mgmt__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-permission-mgmt">
      {error && (
        <div className="rp-permission-mgmt__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      <TabsContext activeTabId={activeTab}>
        <TabList onTabChange={(tabId) => setActiveTab(tabId)}>
          <Tab>Manage Access</Tab>
          <Tab>Restrict Users</Tab>
          <Tab>App Admins</Tab>
        </TabList>

        <TabPanels activeTabId={activeTab}>
          {/* Manage Access */}
          <TabPanel>
            <div className="rp-permission-mgmt__tab-content">
              <ManageAccessTab
                users={users}
                allUsersEnabled={allUsersEnabled}
                onToggleAllUsers={handleToggleAllUsers}
                onAddUser={addUser}
                onRemoveUser={removeUser}
                onUpdateUser={updateUser}
              />
            </div>
          </TabPanel>

          {/* Restrict Users */}
          <TabPanel>
            <div className="rp-permission-mgmt__tab-content">
              <RestrictUsersTab
                users={users}
                onUpdateUser={updateUser}
              />
            </div>
          </TabPanel>

          {/* App Admins */}
          <TabPanel>
            <div className="rp-permission-mgmt__tab-content">
              <AppAdminsTab
                users={users}
                onUpdateUser={updateUser}
              />
            </div>
          </TabPanel>
        </TabPanels>
      </TabsContext>
    </div>
  );
};

export default PermissionManagementPanel;
